import type { IUser } from "types/user.types";

/**
 * "How did you find us?" (EQ-2266). Keys match the API's
 * `src/app/user/signup-attribution.ts`; the API owns the list and the export
 * labels, so an unknown key is displayed raw rather than hidden.
 */
export const SIGNUP_ATTRIBUTION_CHANNELS = [
	{ key: "intuit_app_store", label: "Intuit App Store" },
	{ key: "linkedin", label: "LinkedIn" },
	{ key: "peer_recommendation", label: "Peer Recommendation" },
	{ key: "ai_or_online_search", label: "AI or Online Search" },
	{ key: "other", label: "Other" },
] as const;

export type SignupAttributionChannel =
	(typeof SIGNUP_ATTRIBUTION_CHANNELS)[number]["key"];

export const SIGNUP_ATTRIBUTION_OTHER_MAX = 200;

export const SIGNUP_ATTRIBUTION_ALREADY_RECORDED =
	"SIGNUP_ATTRIBUTION_ALREADY_RECORDED";

export function validateAttribution(
	channel: SignupAttributionChannel | undefined,
	otherText: string,
): string | null {
	if (!channel) return "Choose how you found Equility.";
	if (channel === "other" && !otherText.trim()) {
		return "Tell us where you found Equility.";
	}
	return null;
}

type AttributionFields = Pick<
	IUser,
	"signupAttribution" | "signupAttributionOther" | "signupAttributionRequired"
>;

export function foundUsViaText(user: AttributionFields): string {
	const key = user.signupAttribution;
	if (!key) return user.signupAttributionRequired ? "Pending" : "-";
	const label =
		SIGNUP_ATTRIBUTION_CHANNELS.find((c) => c.key === key)?.label ?? key;
	return key === "other" && user.signupAttributionOther
		? `${label}: ${user.signupAttributionOther}`
		: label;
}

/**
 * The gate may decide only once the server answered, or once SWR stopped
 * retrying. During sign-up the first request can 401 because the user row does
 * not exist yet; treating that as "not required" would skip the question.
 */
export function isOnboardingSettled(state: {
	data: unknown;
	error?: unknown;
	isValidating: boolean;
}): boolean {
	if (state.data !== undefined) return true;
	return Boolean(state.error) && !state.isValidating;
}
